import React from "react";

/**
 * Política de Privacidad — Turismo Madre de Dios
 * Diseñada para cumplir requisitos de Google Play Store (mención explícita
 * de datos personales, GPS, y contacto). Ruta: /politica-privacidad
 */
export default function PrivacyPolicyView() {
  const APP_NAME = "Turismo Madre de Dios";
  const CONTACT_EMAIL = "hugo@turismomdd.pe";
  const COMPANY = "Turismo Madre de Dios S.A.C.";
  const UPDATED = "8 de septiembre de 2026";

  return (
    <div className="min-h-screen bg-white text-neutral-900 py-12 px-6" data-testid="privacy-policy-view">
      <div className="max-w-3xl mx-auto">
        <div className="border-b-4 border-[#C6E90C] pb-6 mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-[#3949AB]">Política de Privacidad</div>
          <h1 className="font-display font-black text-4xl md:text-5xl leading-tight mt-2">{APP_NAME}</h1>
          <div className="text-sm text-neutral-600 mt-3">Última actualización: {UPDATED}</div>
        </div>

        <Section title="1. Introducción">
          <p>
            En <b>{APP_NAME}</b> ("nosotros", "la empresa", "la aplicación") nos comprometemos a proteger la privacidad
            de los usuarios ("usted", "el pasajero", "el conductor", "el socio") que utilizan nuestras aplicaciones
            móviles (App Pasajero y App Conductor) y nuestra plataforma web. Esta Política de Privacidad describe qué
            información recopilamos, cómo la usamos, con quién la compartimos y qué derechos tiene sobre ella.
          </p>
          <p>
            Esta política aplica a las siguientes aplicaciones publicadas en Google Play y Apple App Store:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><b>Turismo Madre de Dios · Pasajero</b> (package: <code>com.turismo.madrededios.pasajero</code>)</li>
            <li><b>Turismo Madre de Dios · Conductor</b> (package: <code>com.turismo.madrededios.conductor</code>)</li>
          </ul>
        </Section>

        <Section title="2. Información que recopilamos">
          <h3 className="font-bold text-lg mt-4">2.1. Información que usted nos proporciona</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Nombre completo, correo electrónico y número de teléfono al registrarse.</li>
            <li>Datos del vehículo (placa, marca, modelo, color) en el caso de conductores y socios.</li>
            <li>Fotos de perfil, licencia de conducir y tarjeta de propiedad (opcionales).</li>
            <li>Direcciones de origen, destino y favoritos que usted guarde en la app.</li>
            <li>Mensajes intercambiados con nuestro bot de WhatsApp y con la central de {APP_NAME}.</li>
          </ul>

          <h3 className="font-bold text-lg mt-4">2.2. Información recopilada automáticamente</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <b>Ubicación GPS (precisa y aproximada):</b> tanto en primer plano como en segundo plano en el caso de
              los conductores durante una carrera activa. Esta información es indispensable para asignar el chofer más
              cercano, mostrar la ruta en tiempo real al pasajero y calcular la tarifa por distancia recorrida.
              El pasajero puede desactivar la recolección de ubicación desde los ajustes del sistema operativo, pero
              hacerlo limitará funcionalidades clave como la solicitud rápida desde su posición actual.
            </li>
            <li><b>Identificador del dispositivo</b> (Device ID) y token de notificaciones push (OneSignal / FCM).</li>
            <li>Dirección IP, tipo de dispositivo, sistema operativo, versión de la app y datos de diagnóstico.</li>
            <li>Historial de viajes: origen, destino, tarifa, chofer asignado, calificaciones, incidencias.</li>
          </ul>
        </Section>

        <Section title="3. Cómo usamos su información">
          <ul className="list-disc pl-6 space-y-1">
            <li><b>Prestar el servicio de taxi:</b> conectar pasajeros con conductores, cobrar la tarifa correcta y notificar el arribo del vehículo.</li>
            <li><b>Ubicación GPS del conductor:</b> se usa exclusivamente durante una carrera activa para mostrar al pasajero la posición del vehículo en tiempo real y calcular la ruta óptima. La recolección en segundo plano cesa automáticamente al finalizar o cancelar la carrera.</li>
            <li><b>Ubicación GPS del pasajero:</b> se usa para autocompletar el origen del viaje y asignar el chofer más cercano. No se almacena histórico de coordenadas más allá de los datos del viaje.</li>
            <li><b>Notificaciones push:</b> avisar al conductor de nuevas solicitudes, y al pasajero del arribo y estado del viaje.</li>
            <li><b>Prevención de fraude:</b> detectar cuentas duplicadas por número de teléfono, aplicar promociones responsables y evitar abusos de cancelación.</li>
            <li><b>Cumplimiento legal:</b> atender requerimientos de autoridades peruanas competentes cuando la ley lo exija.</li>
          </ul>
        </Section>

        <Section title="4. Con quién compartimos su información">
          <p>{APP_NAME} <b>no vende</b> datos personales a terceros. Compartimos información sólo con:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><b>Conductor asignado a su carrera:</b> nombre, teléfono y punto de recojo (sólo mientras el viaje está activo).</li>
            <li><b>Pasajero solicitante:</b> nombre, patente, modelo y teléfono del conductor asignado.</li>
            <li><b>Proveedores tecnológicos:</b> MongoDB Atlas (base de datos), OneSignal (notificaciones), 2chat.io (WhatsApp), OpenStreetMap/Overpass (búsqueda de lugares) y OSRM (ruteo por carretera). Ninguno recibe datos con fines de marketing.</li>
            <li><b>Autoridades competentes:</b> únicamente cuando exista orden judicial o requerimiento legal.</li>
          </ul>
        </Section>

        <Section title="5. Retención de datos">
          <p>
            Retenemos los datos personales durante el tiempo necesario para prestar el servicio. Los registros de viajes
            se mantienen por un plazo de <b>hasta 5 años</b> por motivos fiscales y de auditoría. Puede solicitar la
            eliminación de su cuenta y sus datos en cualquier momento escribiendo a <a className="text-[#3949AB] underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </Section>

        <Section title="6. Sus derechos">
          <p>Como usuario tiene los siguientes derechos, garantizados por la Ley de Protección de Datos Personales (Ley 29733) del Perú:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><b>Acceso:</b> obtener una copia de sus datos personales.</li>
            <li><b>Rectificación:</b> corregir datos inexactos.</li>
            <li><b>Cancelación:</b> solicitar la eliminación de su cuenta y sus datos.</li>
            <li><b>Oposición:</b> negarse al tratamiento con fines específicos (por ejemplo, notificaciones promocionales).</li>
            <li><b>Portabilidad:</b> recibir sus datos en un formato estructurado y legible.</li>
          </ul>
          <p>Para ejercer estos derechos escríbanos a <a className="text-[#3949AB] underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>
          <p>
            Para <b>eliminar tu cuenta y datos personales</b>, visita nuestra página dedicada:{" "}
            <a className="text-[#3949AB] underline" href="/delete-account" data-testid="privacy-delete-account-link">
              Solicitar eliminación de cuenta →
            </a>
          </p>
        </Section>

        <Section title="7. Seguridad">
          <p>
            {APP_NAME} implementa medidas técnicas y organizativas razonables para proteger sus datos: comunicación
            cifrada HTTPS/TLS 1.3, autenticación con hash bcrypt y JWT, almacenamiento en infraestructura MongoDB Atlas
            con backups automáticos y control de acceso basado en roles. Sin embargo, ningún sistema es 100% inmune;
            usted es responsable de mantener seguros su contraseña y dispositivo.
          </p>
        </Section>

        <Section title="8. Menores de edad">
          <p>
            Nuestros servicios no están dirigidos a menores de 14 años. No recopilamos deliberadamente información
            personal de menores. Si detecta que un menor ha creado una cuenta, contáctenos y procederemos a eliminarla.
          </p>
        </Section>

        <Section title="9. Cambios a esta política">
          <p>
            Podemos actualizar esta Política de Privacidad. Publicaremos la versión vigente en esta misma URL con la
            fecha de última actualización. El uso continuado de la app tras un cambio implica su aceptación.
          </p>
        </Section>

        <Section title="10. Contacto">
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 mt-2">
            <div className="font-bold">{COMPANY}</div>
            <div className="text-sm mt-1">Puerto Maldonado, Madre de Dios, Perú</div>
            <div className="text-sm mt-1">Correo: <a className="text-[#3949AB] underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></div>
            <div className="text-sm mt-1">Web: <a className="text-[#3949AB] underline" href="https://turismo.mdd.analytica-ec.com">turismo.mdd.analytica-ec.com</a></div>
          </div>
        </Section>

        <div className="border-t border-neutral-200 mt-12 pt-6 flex items-center justify-between text-xs text-neutral-500">
          <div>© {new Date().getFullYear()} {COMPANY}. Todos los derechos reservados.</div>
          <a href="/" className="text-[#3949AB] hover:underline">← Volver al inicio</a>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-6 leading-relaxed">
      <h2 className="font-display font-bold text-2xl border-l-4 border-[#C6E90C] pl-3 mt-8 mb-3">{title}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
